import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Grid,
  Stack,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import { Save } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { createLandmark } from "../../api/landmark";
import { listStations } from "../../api/station";
import { Station } from "../../types/api";

import ImageUploadTab, { ImageItem } from "../../components/landmark/ImageUploadTab";
import { TabPanel } from "../../components/TabPanel";
import { uploadMultiFile } from "../../api/file";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FlyToLocation({ position }: { position: [number, number] }) {
  const map = useMap();
  map.flyTo(position, map.getZoom());
  return null;
}

export default function LandmarkCreatePage() {
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);

  const [nameTh, setNameTh] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameZh, setNameZh] = useState("");
  const [description, setDescription] = useState("");

  const [latInput, setLatInput] = useState("18.74485267563325");
  const [lngInput, setLngInput] = useState("98.92795715183036");
  const [markerPos, setMarkerPos] = useState<[number, number]>([
    18.74485267563325,
    98.92795715183036,
  ]);
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);

  const [stations, setStations] = useState<Station[]>([]);
  const [stationId, setStationId] = useState<number | "">("");

  const [imageItems, setImageItems] = useState<ImageItem[]>([]);

  useEffect(() => {
    listStations().then(setStations);
  }, []);

  const handleSubmit = async () => {
    try {
      if (!stationId) {
        alert("กรุณาเลือกสถานีใกล้เคียง");
        return;
      }

      // ===== upload รูป =====
      const newImages = imageItems.filter((x) => x.file);
      const uploaded = newImages.length
        ? await uploadMultiFile(newImages.map((x) => x.file!))
        : [];

      let upIdx = 0;
      const merged = imageItems.map((img) => {
        if (!img.file) return img;

        const url = uploaded[upIdx]?.imagePath;
        upIdx++;

        return {
          ...img,
          imagePath: url,
          file: undefined,
        };
      });

      const payload = {
        nameTh,
        nameEn,
        nameZh,
        description,
        latitude: parseFloat(latInput),
        longitude: parseFloat(lngInput),
        stationId,
        isDisabled: false,

        landmarkImages: merged
          .filter((x) => x.imagePath)
          .map((x, idx) => ({
            imagePath: x.imagePath!,
            title: x.title?.trim() || null,
            orderSeq: idx,
          })),
      };

      await createLandmark(payload);

      alert("สร้าง Landmark สำเร็จ ✅");
      navigate("/landmarks");
    } catch (err: any) {
      alert("สร้าง Landmark ล้มเหลว: " + err.message);
    }
  };

  function MapClickHandler({ setMarker }: { setMarker: (pos: [number, number]) => void }) {
    useMapEvents({
      click(e) {
        const pos: [number, number] = [e.latlng.lat, e.latlng.lng];
        setMarker(pos);
      },
    });
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        เพิ่ม Landmark ใหม่
      </Typography>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="ข้อมูลสถานที่" />
        <Tab label={`รูปภาพ (${imageItems.length})`} />
      </Tabs>

      <Paper sx={{ p: 3, mt: 2 }}>
        {/* TAB 1 */}
        <TabPanel value={tab} index={0}>
          <Stack gap={3}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField fullWidth label="ชื่อ (ไทย)" value={nameTh} onChange={(e) => setNameTh(e.target.value)} />
              </Grid>
              <Grid size={4}>
                <TextField fullWidth label="ชื่อ (อังกฤษ)" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
              </Grid>
              <Grid size={4}>
                <TextField fullWidth label="ชื่อ (จีน)" value={nameZh} onChange={(e) => setNameZh(e.target.value)} />
              </Grid>

              <Grid size={12}>
                <TextField
                  select
                  fullWidth
                  label="สถานีใกล้เคียง"
                  value={stationId}
                  onChange={(e) => setStationId(Number(e.target.value))}
                >
                  {stations.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.nameTh} ({st.nameEn})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={6}>
                <TextField fullWidth label="Latitude" value={latInput} onChange={(e) => setLatInput(e.target.value)} />
              </Grid>
              <Grid size={6}>
                <TextField fullWidth label="Longitude" value={lngInput} onChange={(e) => setLngInput(e.target.value)} />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="รายละเอียดสถานที่"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
            </Grid>

            <Box sx={{ height: 400 }}>
              <MapContainer center={markerPos} zoom={16} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={markerPos} />
                {flyTarget && <FlyToLocation position={flyTarget} />}
                <MapClickHandler
                  setMarker={(pos) => {
                    setMarkerPos(pos);
                    setLatInput(pos[0].toString());
                    setLngInput(pos[1].toString());
                  }}
                />
              </MapContainer>
            </Box>

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" startIcon={<Save />} onClick={handleSubmit}>
                บันทึก
              </Button>
            </Box>
          </Stack>
        </TabPanel>

        {/* TAB 2 */}
        <TabPanel value={tab} index={1}>
          <ImageUploadTab maxFiles={10} value={imageItems} onChange={setImageItems} />

          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSubmit}>
              บันทึก
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
}
